<template>
  <page-layout :readonly="isProposalAccepted" :hide-footer="isProposalAccepted">
    <g-loader type="fixed" v-if="isLoading" />
    <template slot="header">
      <proposal-step-header
        :current-step="stepNumber"
        :total-steps="totalSteps"
        :title="compensationTitle"
        :use-exit-modal="false"
        :is-loading="isLoading"
      />
    </template>
    <template slot="default">
      <div
        v-if="compensationMethods.length > 1 || isCustomCompensationDescription"
        class="margin-bottom-large"
      >
        <g-typography tag="p" :value="compensationDescription" class="break-new-line" />
      </div>
      <proposal-step-error
        class="margin-bottom-normal"
        v-if="proposalStep && proposalStep.error"
        :error="proposalStep.error"
      />
      <template v-if="hasCompensationMethod('money')">
        <div class="margin-bottom-large">
          <div class="margin-bottom-small">
            <g-typography tag="h4" font-weight="semibold" value="Payment" />
          </div>
          <template v-if="campaign.inputs_price">
            <div class="margin-bottom-normal">
              <g-typography
                tag="p"
                :value="customPaymentDescription"
                class="break-new-line"
              />
            </div>
            <div class="margin-bottom-normal">
              <g-form>
                <g-form-item :errors="errors.price">
                  <g-input
                    v-model="price"
                    size="large"
                    type="number"
                    min="0"
                    @change="handlePriceChange"
                  >
                    <template slot="prefix" v-if="currencySymbol" class="test-class">
                      <g-icon
                        v-if="currencySymbol.type === 'icon'"
                        :name="currencySymbol.value"
                      />
                      <span v-else>{{ currencySymbol.value }}</span>
                    </template>
                  </g-input>
                </g-form-item>
              </g-form>
            </div>
          </template>
          <template v-else>
            <div class="margin-bottom-normal">
              <g-typography
                tag="p"
                :value="paymentDescription"
                class="break-new-line"
              />
            </div>
          </template>
        </div>
      </template>
      <template v-if="hasCompensationMethod('affiliate')">
        <div class="margin-bottom-large">
          <div class="margin-bottom-small">
            <g-typography tag="h4" font-weight="semibold" value="Commission" />
          </div>
          <div class="margin-bottom-normal">
            <g-typography
              tag="p"
              :value="commissionDescription"
              class="break-new-line"
            />
          </div>

          <template v-if="campaign.show_revenue_tracking_information">
            <template v-if="affiliateLink">
              <float-on-mask
                :float-up="isProposalAccepted"
                tag="div"
                class="margin-bottom-small"
              >
                <link-code-box type="link" :text="affiliateLink" />
              </float-on-mask>
            </template>
            <template v-if="showDiscountCodes" v-for="discountCode in discountCodes">
              <float-on-mask
                :float-up="isProposalAccepted"
                tag="div"
                class="margin-bottom-small"
              >
                <link-code-box type="code" :text="discountCode" />
              </float-on-mask>
            </template>
          </template>
        </div>
      </template>
      <template v-if="hasCompensationMethod('product')">
        <div class="margin-bottom-large">
          <div class="margin-bottom-small">
            <g-typography tag="h4" font-weight="semibold" value="Product" />
          </div>
          <div class="margin-bottom-normal">
            <g-typography tag="p" :value="productDescription" class="break-new-line" />
          </div>
        </div>
      </template>
    </template>
    <template slot="footer">
      <proposal-step-footer
        :is-next-disabled="isDisabled"
        :loading="isCompletingProposal"
        :use-exit-modal="false"
        :step-completed="isCompleted"
        :current-step="stepNumber"
        :total-steps="totalSteps"
        @go-next-step="$emit('go-next-step', proposalStep)"
        @go-previous-step="$emit('go-previous-step', proposalStep)"
      />
    </template>
  </page-layout>
</template>
<script>
  import get from 'lodash/get';
  import isEmpty from 'lodash/isEmpty';
  import debounce from 'lodash/debounce';
  import PageLayout from '../../layouts/PageLayout';
  import ProposalStepHeader from './partials/ProposalStepHeader';
  import ProposalStepFooter from './partials/ProposalStepFooter';
  import { currencies } from '@grininc/grin-ui';
  import LinkCodeBox from './partials/LinkCodeBox';
  import { NAME_COMPENSATION } from '../../../constants/proposal-steps';
  import ProposalStepError from './partials/PoposalStepError';
  import CompensationMixin from './mixins/CompensationMixin';
  import { getCustomizableTitle } from '../../../helpers/default-texts';
  import ProposalMixin from './mixins/ProposalMixin';
  import FloatOnMask from '../../library/FloatOnMask';

  export default {
    name: 'ProposalCompensationView',
    components: {
      FloatOnMask,
      ProposalStepError,
      LinkCodeBox,
      PageLayout,
      ProposalStepHeader,
      ProposalStepFooter
    },
    mixins: [CompensationMixin, ProposalMixin],
    props: {
      campaign: {
        type: Object
      },
      proposal: {
        type: Object
      },
      proposalStep: {
        type: Object
      },
      affiliateLink: {
        type: String
      },
      discountCodes: {
        type: Array,
        default: []
      },
      isLoading: {
        type: Boolean,
        default: false
      },
      isCompletingProposal: {
        type: Boolean,
        default: false
      },
      errors: {
        type: Object,
        default() {
          return {};
        }
      }
    },
    data() {
      return {
        price: ''
      };
    },
    computed: {
      currencySymbol() {
        if (
          Object.prototype.hasOwnProperty.call(currencies, this.campaign.brand.currency)
        ) {
          if (
            Object.prototype.hasOwnProperty.call(
              currencies[this.campaign.brand.currency],
              'fa_symbol'
            )
          ) {
            return {
              type: 'icon',
              value: currencies[this.campaign.brand.currency].fa_symbol
            };
          }

          return {
            type: 'text',
            value: currencies[this.campaign.brand.currency].symbol
          };
        }

        return false;
      },
      compensationTitle() {
        return getCustomizableTitle(this.campaign, 'compensation_step_title');
      },
      paymentDescription() {
        const description = get(
          this,
          'campaign.customization.compensation_payment_overview'
        );
        return description ? description : "You'll be paid a fixed amount.";
      },
      commissionDescription() {
        const description = get(
          this,
          'campaign.customization.compensation_commission_overview'
        );
        return description
          ? description
          : "You'll be paid for every time a purchase is made with your affiliate link or discount code.";
      },
      productDescription() {
        const description = get(this, 'campaign.compensation_settings.product_text');
        return description
          ? description
          : "You'll receive products for your participation in this campaign.";
      },
      isDisabled() {
        return !get(this, 'proposalStep.is_valid', false) || this.hasErrors;
      },
      hasErrors() {
        return this.errors !== undefined && !isEmpty(this.errors);
      },
      stepNumber() {
        return get(this, 'proposalStep.meta.stepNumber', 0);
      },
      totalSteps() {
        return get(this, 'proposalStep.meta.totalSteps', 0);
      },
      isCompleted() {
        return get(this, 'proposalStep.is_complete', false);
      },
      showDiscountCodes() {
        return get(this, 'campaign.brand.show_discount_codes', false);
      }
    },
    methods: {
      hasCompensationMethod(method) {
        return get(this, 'campaign.compensation', []).includes(method);
      },
      handlePriceChange: debounce(function () {
        if (!isEmpty(this.price)) {
          this.$emit('patch-step', {
            step: NAME_COMPENSATION,
            complete: false,
            payload: { price: this.price }
          });
        }
      }, 1000)
    },
    watch: {
      proposal() {
        this.price = get(this, 'proposal.price', '');
      },
      price() {
        if (this.price === '') {
          this.$set(this.errors, 'price', ['Invalid price']);
        }
      }
    },
    created() {
      this.price = get(this, 'proposal.price', '');
    }
  };
</script>

<style lang="scss" scoped>
  .margin-bottom-small {
    margin-bottom: $g-spacing-3;
  }

  .margin-bottom-normal {
    margin-bottom: $g-spacing-5;
  }

  .margin-bottom-large {
    margin-bottom: $g-spacing-6;
  }

  .break-new-line {
    white-space: pre-line;
  }
</style>
